<script>
import isEmpty from 'lodash.isempty'
import List from '@/components/organism/restaurants/List'
import MenuRow from '@/components/molecules/MenuRow'
import RestaurantsHeading from '@/components/organism/restaurants/RestaurantsHeading'
import StoreBlock from '@/components/organism/brand/StoreBlock'

// Smallest known distance among a group's members; null when none of them has one.
function nearestDistance(items) {
    const distances = items
        .map((item) => (item.delivery ? item.delivery.distance : null))
        .filter((distance) => typeof distance === 'number')
    return distances.length ? Math.min(...distances) : null
}

export default {
    name: 'BrandStoreList',

    components: { List, MenuRow, RestaurantsHeading, StoreBlock },

    props: {
        restaurants: {
            type: Object,
            default: () => ({}),
        },
        setList: Function,
    },

    computed: {
        hasAddress() {
            return !isEmpty(this.$store.getters['order/address'])
        },
        // The brand list is fetched with BRAND_LIST_LIMIT (200, see restaurantList.js).
        // `rows` drops the null holes of an UNFINISHED fetch, so isFullyLoaded (below)
        // must gate the store layout: past the limit this component falls back to the
        // paginated `list`, which pages further through `getPage` (List.vue).
        rows() {
            return (this.restaurants.items || []).filter(Boolean)
        },
        // PagedCollection.total is 0 before the first response (so the loading state
        // still goes to the fallback, exactly as before) and the server-reported total
        // afterwards; once every row up to it has loaded (no more holes), rows.length
        // reaches it too.
        isFullyLoaded() {
            return this.restaurants.total <= this.rows.length
        },
        // Consecutive rows sharing a store form one group (the server keeps them
        // contiguous); store-less rows are collected separately. Without an address the
        // server orders by restaurant name, so the groups are re-sorted by store name.
        grouped() {
            const groups = []
            const others = []
            let current = null
            this.rows.forEach((row) => {
                if (!row.store) {
                    others.push(row)
                    current = null
                    return
                }
                if (!current || current.store.id !== row.store.id) {
                    current = { store: row.store, items: [] }
                    groups.push(current)
                }
                current.items.push(row)
            })
            groups.forEach((group) => {
                group.distance = nearestDistance(group.items)
            })
            if (!this.hasAddress) {
                groups.sort((a, b) => (a.store.name || '').localeCompare(b.store.name || ''))
            }
            return { groups, others }
        },
        groups() {
            return this.grouped.groups
        },
        others() {
            return this.grouped.others
        },
        hasStores() {
            return this.groups.length > 0
        },
    },
}
</script>

<template lang="pug">
.brand-store-list
    template(v-if="hasStores && isFullyLoaded")
        restaurants-heading(:restaurants="restaurants" brand)
        .brand-store-grid
            store-block(v-for="(group, index) in groups" :key="`${group.store.id}-${index}`" :group="group" :nearest="hasAddress && index === 0" :delay="restaurants.delay")
            .store-block.store-block-others(v-if="others.length")
                .store-block-head
                    h3.store-block-name {{ $t('brand.other_options') }}
                .store-block-menus
                    menu-row(v-for="item in others" :key="item.id" :model="item" :delay="restaurants.delay")
    template(v-else)
        list(:restaurants="restaurants" :set-list="setList" brand)
</template>
